import React, { useState, useEffect } from 'react';
import { Globe, Mail, Phone, Calendar, HelpCircle } from 'lucide-react';
import { useTranslation } from '../../../../contexts/LanguageContext';
import { useAuth } from '../../../../App';

const MobileMerchantDetailsTab = ({ merchant }) => {
  const { t } = useTranslation();
  const { httpClient } = useAuth();
  const [affiliateLink, setAffiliateLink] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAffiliateLink = async () => {
      if (!merchant?.id) return;
      
      setLoading(true);
      try {
        // Use httpClient which handles the base URL
        const params = new URLSearchParams({
          merchant_id: merchant.id,
          link_type: 'textlink',
          plan_id: 'null'
        });
        
        const response = await httpClient.request(
          `/api/merchant-affiliate-links?${params}`
        );
        
        if (response.ok) {
          const links = await response.json();
          if (links.length > 0) {
            setAffiliateLink(links[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching affiliate link:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAffiliateLink();
  }, [merchant, httpClient]);

  const formatDate = (dateString) => {
    if (!dateString || dateString === "No Data") return t('common.na') || "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleWebsiteClick = async (e) => {
    if (affiliateLink) {
      try {
        // Use httpClient for tracking
        await httpClient.request('/api/track-affiliate-click', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            affiliate_link_id: affiliateLink._id,
            merchant_id: merchant.id,
            plan_id: null
          }),
        });
      } catch (error) {
        console.error('Error tracking affiliate click:', error);
      }
      
      window.open(affiliateLink.url, '_blank');
      e.preventDefault();
    }
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 space-y-4">
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-[#253985] flex items-center gap-2">
          <Globe className="h-5 w-5" />
          {t('merchant.detailsTab.companyInformation')}
        </h2>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Globe className="h-5 w-5 text-[#253985] mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">
                {t('merchant.common.website')}
              </p>
              {loading ? (
                <div className="h-4 w-40 bg-gray-200 animate-pulse rounded"></div>
              ) : affiliateLink ? (
                <a 
                  href={affiliateLink.url} 
                  onClick={handleWebsiteClick}
                  rel="sponsored noopener noreferrer"
                  target="_blank"
                  className="text-[#253985] text-sm break-all"
                >
                  {merchant.website}
                </a>
              ) : (
                <a 
                  href={merchant.website} 
                  className="text-[#253985] text-sm break-all"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {merchant.website}
                </a>
              )}
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-[#253985] mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">
                {t('merchant.common.contactEmail')}
              </p>
              <a 
                href={`mailto:${merchant.email}`}
                className="text-[#253985] text-sm break-all"
              >
                {merchant.email}
              </a>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="h-5 w-5 text-[#253985] mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">
                {t('merchant.common.phone')}
              </p>
              <p className="text-[#253985] text-sm">{merchant.phone}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-[#253985] mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">
                {t('merchant.common.founded')}
              </p>
              <p className="text-[#253985] text-sm">
                {formatDate(merchant.founded)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-[#253985] flex items-center gap-2">
          <HelpCircle className="h-5 w-5" />
          {t('merchant.detailsTab.about')}
        </h2>
        <p className="text-gray-700 text-sm">{merchant.description}</p>
        <div className="bg-blue-100 text-[#253985] px-3 py-1 rounded-full w-max text-sm">
          {merchant.category}
        </div>
      </div>
    </div>
  );
};

export default MobileMerchantDetailsTab;